import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Table, Button, InputNumber, Space, Tag, Typography,
  Modal, message, Statistic, Row, Col,
  Popconfirm, Input, Form, Tabs, Alert, Select, Segmented, Grid, Empty, Collapse
} from 'antd';
import {
  DollarOutlined, GoldOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ClockCircleOutlined, UserOutlined, ReloadOutlined,
  ExportOutlined, CrownOutlined, EditOutlined, BankOutlined, SaveOutlined, SwapOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

const API_BASE_URL = '';
const MESO_FEE_RATE = 0.025; // 2.5%
const { useBreakpoint } = Grid;

const formatDate = (t) => {
  if (!t) return '未知日期';
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const BankCounter = ({ currentUser }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [withdrawals, setWithdrawals] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [userBalance, setUserBalance] = useState({ meso: 0, coin: 0 });
  const [allBalances, setAllBalances] = useState([]);  // 全部用户余额（管理员代提用）
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // 统一「新申请」弹窗（提取 / 转账 / 代理提取）
  const [applyModalVisible, setApplyModalVisible] = useState(false);
  const [applyMode, setApplyMode] = useState('withdraw'); // 'withdraw' | 'transfer' | 'proxy'
  const [applyForm] = Form.useForm();
  const [applyTarget, setApplyTarget] = useState(null);   // 转账收款人 / 代提目标用户 的余额对象

  // 提取审核弹窗
  const [processModalVisible, setProcessModalVisible] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [processForm] = Form.useForm();

  // 转账相关
  const [transfers, setTransfers] = useState([]);              // 与自己相关的转账（发起+收到）
  const [pendingTransfers, setPendingTransfers] = useState([]); // 管理员待审
  const [transferProcessVisible, setTransferProcessVisible] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [transferProcessForm] = Form.useForm();

  // 兑换相关
  const [exchanges, setExchanges] = useState([]);
  const [pendingExchanges, setPendingExchanges] = useState([]);
  const [exchangeProcessVisible, setExchangeProcessVisible] = useState(false);
  const [selectedExchange, setSelectedExchange] = useState(null);
  const [exchangeProcessForm] = Form.useForm();

  // 系统配置（汇率）
  const [sysConfig, setSysConfig] = useState({ exchangeMesoToCoin: 0, exchangeCoinToMeso: 0 });
  const [rateModalVisible, setRateModalVisible] = useState(false);
  const [rateForm] = Form.useForm();

  // 银行角色相关
  const [bankCharacter, setBankCharacter] = useState('');
  const [editingBankChar, setEditingBankChar] = useState(false);
  const [bankCharInput, setBankCharInput] = useState('');
  const [bankCharInfo, setBankCharInfo] = useState(null);
  const [bankCharLoading, setBankCharLoading] = useState(false);

  // 是否是管理员
  const isAdmin = useMemo(() => {
    return currentUser && admins.includes(currentUser.username);
  }, [currentUser, admins]);

  // 提交代理提取（管理员专用）
  // 取消自己的 pending 转账
  const handleCancelTransfer = async (id) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/bonus-bank/transfers/${id}?username=${encodeURIComponent(currentUser.username)}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        messageApi.success('转账已取消');
        loadMyTransfers();
        if (isAdmin) loadPendingTransfers();
      } else {
        const d = await res.json();
        messageApi.error(d.detail || '取消失败');
      }
    } catch (e) {
      messageApi.error('取消失败');
    }
  };

  // 管理员处理转账
  const handleProcessTransfer = async (values) => {
    if (!selectedTransfer) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/bonus-bank/transfers/${selectedTransfer.id}?admin_username=${encodeURIComponent(currentUser.username)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: values.action, adminNote: values.adminNote || '' }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        messageApi.success(values.action === 'approve' ? '转账已批准' : '转账已拒绝');
        setTransferProcessVisible(false);
        setSelectedTransfer(null);
        transferProcessForm.resetFields();
        loadMyTransfers();
        loadPendingTransfers();
        loadUserBalance();
        loadAllBalances();
      } else {
        messageApi.error(data.detail || '处理失败');
      }
    } catch (e) {
      messageApi.error('处理失败');
    }
    setLoading(false);
  };

  // 取消自己的 pending 兑换
  const handleCancelExchange = async (id) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/bonus-bank/exchanges/${id}?username=${encodeURIComponent(currentUser.username)}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        messageApi.success('兑换已取消');
        loadMyExchanges();
        if (isAdmin) loadPendingExchanges();
      } else {
        const d = await res.json();
        messageApi.error(d.detail || '取消失败');
      }
    } catch (e) {
      messageApi.error('取消失败');
    }
  };

  // 管理员处理兑换
  const handleProcessExchange = async (values) => {
    if (!selectedExchange) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/bonus-bank/exchanges/${selectedExchange.id}?admin_username=${encodeURIComponent(currentUser.username)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: values.action, adminNote: values.adminNote || '' }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        messageApi.success(values.action === 'approve' ? '兑换已批准' : '兑换已拒绝');
        setExchangeProcessVisible(false);
        setSelectedExchange(null);
        exchangeProcessForm.resetFields();
        loadMyExchanges();
        loadPendingExchanges();
        loadUserBalance();
        loadAllBalances();
      } else {
        messageApi.error(data.detail || '处理失败');
      }
    } catch (e) {
      messageApi.error('处理失败');
    }
    setLoading(false);
  };

  // 管理员保存汇率
  const handleSaveRates = async (values) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/system-config?username=${encodeURIComponent(currentUser.username)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            exchangeMesoToCoin: values.exchangeMesoToCoin,
            exchangeCoinToMeso: values.exchangeCoinToMeso,
          }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        messageApi.success('汇率已更新');
        setRateModalVisible(false);
        setSysConfig(data.config);
      } else {
        messageApi.error(data.detail || '更新失败');
      }
    } catch (e) {
      messageApi.error('更新失败');
    }
    setLoading(false);
  };

  // 加载提取申请列表
  const loadWithdrawals = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/withdrawals`);
      if (res.ok) {
        const data = await res.json();
        setWithdrawals(data);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  // 检查当前用户是否是管理员（公开接口，不走 token 鉴权）
  const loadAdmins = useCallback(async () => {
    if (!currentUser?.username) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/${encodeURIComponent(currentUser.username)}/check-admin`);
      if (res.ok) {
        const data = await res.json();
        setAdmins(data.isAdmin ? [currentUser.username] : []);
      }
    } catch (e) {
      console.error(e);
    }
  }, [currentUser?.username]);

  // 加载银行角色设置
  const loadBankCharacter = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/settings/bank-character`);
      if (res.ok) {
        const data = await res.json();
        setBankCharacter(data.characterName || '');
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  // 加载银行角色信息
  const loadBankCharInfo = useCallback(async (charName) => {
    if (!charName) {
      setBankCharInfo(null);
      return;
    }
    setBankCharLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/character?name=${encodeURIComponent(charName)}`);
      if (res.ok) {
        const data = await res.json();
        if (data && data.name) {
          setBankCharInfo(data);
        } else {
          setBankCharInfo(null);
        }
      }
    } catch (e) {
      console.error(e);
      setBankCharInfo(null);
    }
    setBankCharLoading(false);
  }, []);

  // 保存银行角色设置
  const saveBankCharacter = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/settings/bank-character?username=${encodeURIComponent(currentUser?.username)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ characterName: bankCharInput.trim() })
      });
      if (res.ok) {
        setBankCharacter(bankCharInput.trim());
        setEditingBankChar(false);
        messageApi.success('银行角色已更新');
        loadBankCharInfo(bankCharInput.trim());
      } else {
        const data = await res.json();
        messageApi.error(data.detail || '保存失败');
      }
    } catch (e) {
      messageApi.error('保存失败');
    }
  };

  // 加载用户余额
  const loadUserBalance = useCallback(async () => {
    if (!currentUser) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/bonus-bank/balances`);
      if (res.ok) {
        const data = await res.json();
        const userRecord = data.find(u => u.username === currentUser.username);
        if (userRecord) {
          setUserBalance({
            meso: userRecord.mesoBalance || 0,
            coin: userRecord.coinBalance || 0
          });
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [currentUser]);

  // 加载全部用户余额（管理员代提用；接口本身对所有人开放，只是我们只在管理员视角调用）
  const loadAllBalances = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/bonus-bank/balances`);
      if (res.ok) {
        setAllBalances(await res.json());
      }
    } catch (e) { console.error(e); }
  }, []);

  // 我参与的转账（发起 + 收到）
  const loadMyTransfers = useCallback(async () => {
    if (!currentUser?.username) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/bonus-bank/transfers?username=${encodeURIComponent(currentUser.username)}`);
      if (res.ok) setTransfers(await res.json());
    } catch (e) { console.error(e); }
  }, [currentUser]);

  // 管理员待审转账
  const loadPendingTransfers = useCallback(async () => {
    if (!currentUser?.username) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/bonus-bank/pending-transfers?username=${encodeURIComponent(currentUser.username)}`);
      if (res.ok) setPendingTransfers(await res.json());
    } catch (e) { console.error(e); }
  }, [currentUser]);

  // 我的兑换
  const loadMyExchanges = useCallback(async () => {
    if (!currentUser?.username) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/bonus-bank/exchanges?username=${encodeURIComponent(currentUser.username)}`);
      if (res.ok) setExchanges(await res.json());
    } catch (e) { console.error(e); }
  }, [currentUser]);

  // 管理员待审兑换
  const loadPendingExchanges = useCallback(async () => {
    if (!currentUser?.username) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/bonus-bank/pending-exchanges?username=${encodeURIComponent(currentUser.username)}`);
      if (res.ok) setPendingExchanges(await res.json());
    } catch (e) { console.error(e); }
  }, [currentUser]);

  // 系统配置（汇率）
  const loadSysConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/system-config`);
      if (res.ok) setSysConfig(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    loadWithdrawals();
    loadAdmins();
    loadUserBalance();
    loadBankCharacter();
    loadMyTransfers();
    loadMyExchanges();
    loadSysConfig();
  }, [loadWithdrawals, loadAdmins, loadUserBalance, loadBankCharacter, loadMyTransfers, loadMyExchanges, loadSysConfig]);

  // 管理员登录后拉取全用户余额 + 待审转账/兑换
  useEffect(() => {
    if (isAdmin) {
      loadAllBalances();
      loadPendingTransfers();
      loadPendingExchanges();
    }
  }, [isAdmin, loadAllBalances, loadPendingTransfers, loadPendingExchanges]);

  // 当银行角色名变化时，加载角色信息
  useEffect(() => {
    if (bankCharacter) {
      loadBankCharInfo(bankCharacter);
    }
  }, [bankCharacter, loadBankCharInfo]);

  // 提交申请
  // 关闭「新申请」弹窗（三种模式共用）
  const closeApplyModal = () => {
    setApplyModalVisible(false);
    setApplyTarget(null);
    applyForm.resetFields();
  };

  // 统一「新申请」提交（按 applyMode 分派：提取 / 转账 / 代理提取）
  const handleApply = async (values) => {
    if (!currentUser?.username) return;

    // 三种模式的实际提交函数
    const doWithdraw = async () => {
      const res = await fetch(`${API_BASE_URL}/api/withdrawals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: currentUser.username,
          mesoAmount: values.mesoAmount || 0,
          coinAmount: values.coinAmount || 0,
          note: values.note || ''
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '提交失败');
      messageApi.success('提取申请已提交');
      loadWithdrawals();
      loadUserBalance();
    };

    const doTransfer = async () => {
      const res = await fetch(`${API_BASE_URL}/api/bonus-bank/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromUser: currentUser.username,
          toUser: values.targetUser,
          mesoAmount: values.mesoAmount || 0,
          coinAmount: values.coinAmount || 0,
          note: values.note || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '提交失败');
      messageApi.success('转账申请已提交，等待管理员审核');
      loadMyTransfers();
      if (isAdmin) loadPendingTransfers();
    };

    const doProxy = () => {
      // 代理提取需要二次确认（直接划扣，不可撤销）
      Modal.confirm({
        title: '确认代理提取？',
        content: (
          <div>
            <div>目标用户：<Text strong>{values.targetUser}</Text></div>
            <div>Meso：<Text strong style={{ color: '#52c41a' }}>{(values.mesoAmount || 0).toLocaleString()}</Text></div>
            <div>Coin：<Text strong style={{ color: '#faad14' }}>{(values.coinAmount || 0).toLocaleString()}</Text></div>
            <div style={{ marginTop: 8, color: '#ff4d4f' }}>确认后将立即划扣，不可撤销。</div>
          </div>
        ),
        okText: '确认扣款',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: async () => {
          setLoading(true);
          try {
            const res = await fetch(
              `${API_BASE_URL}/api/withdrawals/admin-proxy?admin_username=${encodeURIComponent(currentUser.username)}`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  username: values.targetUser,
                  mesoAmount: values.mesoAmount || 0,
                  coinAmount: values.coinAmount || 0,
                  note: values.note || '',
                }),
              }
            );
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || '代理提取失败');
            messageApi.success(`已为 ${values.targetUser} 完成代理提取`);
            closeApplyModal();
            loadWithdrawals();
            loadUserBalance();
            loadAllBalances();
          } catch (e) {
            messageApi.error(e.message);
          }
          setLoading(false);
        },
      });
    };

    const doExchange = async () => {
      const res = await fetch(`${API_BASE_URL}/api/bonus-bank/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: currentUser.username,
          direction: values.exchangeDirection,
          coinAmount: values.coinAmount || 0,
          note: values.note || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || '提交失败');
      messageApi.success('兑换申请已提交，等待管理员审核');
      loadMyExchanges();
      if (isAdmin) loadPendingExchanges();
    };

    if (applyMode === 'proxy') {
      doProxy();
      return;
    }
    setLoading(true);
    try {
      if (applyMode === 'transfer') await doTransfer();
      else if (applyMode === 'exchange') await doExchange();
      else await doWithdraw();
      closeApplyModal();
    } catch (e) {
      messageApi.error(e.message);
    }
    setLoading(false);
  };

  // 处理申请（批准/拒绝）
  const handleProcess = async (values) => {
    if (!selectedWithdrawal) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/withdrawals/${selectedWithdrawal.id}?admin_username=${currentUser.username}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: values.action,
          adminNote: values.adminNote || ''
        })
      });
      
      if (res.ok) {
        messageApi.success(values.action === 'approve' ? '已批准' : '已拒绝');
        setProcessModalVisible(false);
        processForm.resetFields();
        setSelectedWithdrawal(null);
        loadWithdrawals();
      } else {
        const data = await res.json();
        messageApi.error(data.detail || '处理失败');
      }
    } catch (e) {
      messageApi.error('处理失败');
    }
    setLoading(false);
  };

  // 取消申请
  const handleCancel = async (withdrawalId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/withdrawals/${withdrawalId}?username=${currentUser.username}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        messageApi.success('已取消申请');
        loadWithdrawals();
      } else {
        const data = await res.json();
        messageApi.error(data.detail || '取消失败');
      }
    } catch (e) {
      messageApi.error('取消失败');
    }
  };

  // —— 记录渲染 helper（桌面表格 + 移动端卡片共用） ——
  const renderStatusType = (r) => {
    const statusTag = r.status === 'pending'
      ? <Tag icon={<ClockCircleOutlined />} color="warning">待审核</Tag>
      : r.status === 'approved'
        ? <Tag icon={<CheckCircleOutlined />} color="success">已完成</Tag>
        : <Tag icon={<CloseCircleOutlined />} color="error">已拒绝</Tag>;
    const typeTag = r._type === 'transfer'
      ? <Tag color="purple" icon={<UserOutlined />} style={{ marginTop: 2 }}>转账</Tag>
      : r._type === 'exchange'
        ? <Tag color="gold" icon={<SwapOutlined />} style={{ marginTop: 2 }}>兑换</Tag>
        : <Tag color="geekblue" icon={<ExportOutlined />} style={{ marginTop: 2 }}>提取</Tag>;
    return <Space direction="vertical" size={0}>{statusTag}{typeTag}</Space>;
  };

  const renderAccount = (r) => {
    if (r._type === 'transfer') {
      return (
        <Space direction="vertical" size={0}>
          <Text><Text type="secondary">从 </Text><Text strong>{r.fromUser}</Text></Text>
          <Text><Text type="secondary">到 </Text><Text strong style={{ color: '#1890ff' }}>{r.toUser}</Text></Text>
        </Space>
      );
    }
    if (r._type === 'exchange') {
      return (
        <Space direction="vertical" size={0}>
          <Text strong>{r.username}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{r.direction === 'meso_to_coin' ? 'Meso→Coin' : 'Coin→Meso'}</Text>
        </Space>
      );
    }
    return <Text strong>{r.username}{r.adminProxy && <Tag color="volcano" style={{ marginLeft: 6 }}>代提</Tag>}</Text>;
  };

  const renderAmount = (r) => {
    if (r._type === 'exchange') {
      // 兑换：一侧扣一侧加，展示方向 + 汇率
      return (
        <Space direction="vertical" size={0}>
          {r.direction === 'meso_to_coin'
            ? <Text><Text style={{ color: '#ff4d4f' }}>-{r.mesoAmount.toLocaleString()} Meso</Text> → <Text style={{ color: '#faad14' }}>+{r.coinAmount} Coin</Text></Text>
            : <Text><Text style={{ color: '#ff4d4f' }}>-{r.coinAmount} Coin</Text> → <Text style={{ color: '#52c41a' }}>+{r.mesoAmount.toLocaleString()} Meso</Text></Text>}
          <Text type="secondary" style={{ fontSize: 11 }}>汇率 1C = {(r.rate || 0).toLocaleString()} Meso</Text>
        </Space>
      );
    }
    return (
      <Space direction="vertical" size={0}>
        {r.mesoAmount > 0 && <Text style={{ color: '#52c41a' }}>{r.mesoAmount.toLocaleString()} Meso</Text>}
        {r.coinAmount > 0 && <Text style={{ color: '#faad14' }}>{r.coinAmount.toLocaleString()} Coin</Text>}
        {r._type === 'withdrawal' && r.mesoFee > 0 && (
          <Text type="secondary" style={{ fontSize: 11 }}>手续费 -{r.mesoFee.toLocaleString()}</Text>
        )}
      </Space>
    );
  };

  const renderActions = (r) => {
    if (r.status !== 'pending') return null;
    if (r._type === 'withdrawal') {
      const canCancel = currentUser && r.username === currentUser.username;
      return (
        <Space>
          {isAdmin && (
            <Button size="small" type="primary" onClick={() => {
              setSelectedWithdrawal(r);
              processForm.resetFields();
              setProcessModalVisible(true);
            }}>处理</Button>
          )}
          {canCancel && (
            <Popconfirm title="确定取消此申请？" onConfirm={() => handleCancel(r.id)}>
              <Button size="small" danger>取消</Button>
            </Popconfirm>
          )}
        </Space>
      );
    }
    if (r._type === 'exchange') {
      const canCancel = currentUser && r.username === currentUser.username;
      return (
        <Space>
          {isAdmin && (
            <Button size="small" type="primary" onClick={() => {
              setSelectedExchange(r);
              exchangeProcessForm.resetFields();
              setExchangeProcessVisible(true);
            }}>处理</Button>
          )}
          {canCancel && (
            <Popconfirm title="确认取消这笔兑换？" onConfirm={() => handleCancelExchange(r.id)}>
              <Button size="small" danger>取消</Button>
            </Popconfirm>
          )}
        </Space>
      );
    }
    // transfer
    const canCancel = currentUser && r.fromUser === currentUser.username;
    return (
      <Space>
        {isAdmin && (
          <Button size="small" type="primary" onClick={() => {
            setSelectedTransfer(r);
            transferProcessForm.resetFields();
            setTransferProcessVisible(true);
          }}>处理</Button>
        )}
        {canCancel && (
          <Popconfirm title="确认取消这笔转账？" onConfirm={() => handleCancelTransfer(r.id)}>
            <Button size="small" danger>取消</Button>
          </Popconfirm>
        )}
      </Space>
    );
  };

  // 合并后统一列：类型通过左上角小 Tag 区分，不占独立列
  const unifiedColumns = [
    { title: '状态', width: 100, render: (_, r) => renderStatusType(r) },
    { title: '账户', width: 180, render: (_, r) => renderAccount(r) },
    { title: '金额', width: 200, render: (_, r) => renderAmount(r) },
    { title: '备注', dataIndex: 'note', ellipsis: true },
    { title: '时间', dataIndex: 'createdAt', width: 160, render: (t) => t ? new Date(t).toLocaleString() : '-' },
    { title: '操作', width: 130, fixed: 'right', render: (_, r) => renderActions(r) },
  ];

  // 我的申请
  const myWithdrawals = useMemo(() => {
    if (!currentUser) return [];
    return withdrawals.filter(w => w.username === currentUser.username);
  }, [withdrawals, currentUser]);

  // 待处理申请（管理员视图）
  const pendingWithdrawals = useMemo(() => {
    return withdrawals.filter(w => w.status === 'pending');
  }, [withdrawals]);

  // 合并后的「我的记录」：提取 + 转账 + 兑换
  const myRecords = useMemo(() => {
    const wds = myWithdrawals.map(w => ({ ...w, _type: 'withdrawal' }));
    const trs = transfers.map(t => ({ ...t, _type: 'transfer' }));
    const exs = exchanges.map(e => ({ ...e, _type: 'exchange' }));
    return [...wds, ...trs, ...exs].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  }, [myWithdrawals, transfers, exchanges]);

  // 合并后的「待审核」：提取 + 转账 + 兑换
  const pendingRecords = useMemo(() => {
    const wds = pendingWithdrawals.map(w => ({ ...w, _type: 'withdrawal' }));
    const trs = pendingTransfers.map(t => ({ ...t, _type: 'transfer' }));
    const exs = pendingExchanges.map(e => ({ ...e, _type: 'exchange' }));
    return [...wds, ...trs, ...exs].sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
  }, [pendingWithdrawals, pendingTransfers, pendingExchanges]);

  // 合并后的「全部记录」（管理员）
  const allRecords = useMemo(() => {
    const wds = withdrawals.map(w => ({ ...w, _type: 'withdrawal' }));
    const trs = transfers.map(t => ({ ...t, _type: 'transfer' }));
    const exs = exchanges.map(e => ({ ...e, _type: 'exchange' }));
    return [...wds, ...trs, ...exs].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  }, [withdrawals, transfers, exchanges]);

  // —— 移动端记录卡片 + 列表渲染 ——
  const renderRecordCard = (r) => (
    <Card size="small" key={`${r._type}-${r.id}`} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <div style={{ minWidth: 0 }}>{renderAccount(r)}</div>
        {renderStatusType(r)}
      </div>
      <div style={{ marginBottom: r.note ? 8 : 0 }}>{renderAmount(r)}</div>
      {r.note && (
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>备注：</Text>
          <Text style={{ fontSize: 13 }}>{r.note}</Text>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '-'}</Text>
        {renderActions(r)}
      </div>
    </Card>
  );

  // 记录列表渲染：桌面用表格、移动端用卡片，都按日期分组折叠
  const renderRecords = (records, emptyText) => {
    if (!records.length) {
      return <Empty description={emptyText} style={{ padding: '24px 0' }} />;
    }
    const map = {};
    for (const r of records) {
      const date = formatDate(r.createdAt);
      (map[date] = map[date] || []).push(r);
    }
    const grouped = Object.keys(map).sort().reverse().map(date => ({ date, items: map[date] }));
    const items = grouped.map(g => ({
      key: g.date,
      label: (
        <span>
          <Text strong>{g.date}</Text>
          <Tag style={{ marginLeft: 8 }}>{g.items.length} 条</Tag>
        </span>
      ),
      children: isMobile ? (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {g.items.map(renderRecordCard)}
        </Space>
      ) : (
        <Table
          dataSource={g.items}
          columns={unifiedColumns}
          rowKey={(r) => `${r._type}-${r.id}`}
          size="small"
          pagination={false}
        />
      ),
    }));
    return <Collapse key={grouped[0]?.date} defaultActiveKey={grouped[0]?.date} items={items} />;
  };

  // 计算手续费预览
  const mesoValue = Form.useWatch('mesoAmount', applyForm);
  const previewFee = useMemo(() => {
    if (!mesoValue || mesoValue <= 0) return 0;
    return Math.round(mesoValue * MESO_FEE_RATE * 100) / 100;
  }, [mesoValue]);

  // 兑换预览
  const exDirection = Form.useWatch('exchangeDirection', applyForm);
  const exCoin = Form.useWatch('coinAmount', applyForm);
  const exchangePreview = useMemo(() => {
    const dir = exDirection || 'meso_to_coin';
    const rate = dir === 'meso_to_coin' ? sysConfig.exchangeMesoToCoin : sysConfig.exchangeCoinToMeso;
    const coin = parseInt(exCoin, 10) || 0;
    const meso = Math.round(coin * (rate || 0));
    return { dir, rate: rate || 0, coin, meso };
  }, [exDirection, exCoin, sysConfig]);

  return (
    <div style={{ padding: 16 }}>
      {contextHolder}
      
      {/* 用户信息和银行角色卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={19}>
          <Card style={{ height: '100%' }}>
            <Row gutter={[16, 12]} align="middle">
              <Col xs={12} md={6}>
                <Statistic
                  title="我的 Meso 余额" 
                  value={userBalance.meso} 
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                  formatter={(v) => v.toLocaleString()}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title="我的 Coin 余额"
                  value={userBalance.coin} 
                  prefix={<GoldOutlined />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
              <Col xs={24} md={12} style={{ textAlign: isMobile ? 'left' : 'right' }}>
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<ExportOutlined />}
                    onClick={() => {
                      applyForm.resetFields();
                      setApplyTarget(null);
                      // 普通用户默认「提取」；管理员也默认「提取」，可切「转账/代提取」
                      setApplyMode('withdraw');
                      if (isAdmin && !allBalances.length) loadAllBalances();
                      else if (!allBalances.length) loadAllBalances();
                      setApplyModalVisible(true);
                    }}
                    disabled={!currentUser}
                  >
                    新申请
                  </Button>
                  <Button icon={<ReloadOutlined />} onClick={() => { loadWithdrawals(); loadUserBalance(); loadMyTransfers(); if (isAdmin) loadPendingTransfers(); loadAllBalances(); }}>
                    刷新
                  </Button>
                </Space>
              </Col>
            </Row>
            
            <Alert
              message="提取说明"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Meso 提取收取 <Text strong style={{ color: '#ff4d4f' }}>2.5%</Text> 手续费</li>
                  <li>Coin 提取 <Text strong style={{ color: '#52c41a' }}>免手续费</Text></li>
                  <li>提取申请需要管理员审核后才会从余额中扣除</li>
                </ul>
              }
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          </Card>
        </Col>
        
        {/* 银行角色卡片 */}
        <Col xs={24} md={5}>
          <Card 
            title={<><BankOutlined /> 银行角色</>}
            style={{ height: '100%' }}
            extra={
              isAdmin && !editingBankChar && (
                <Button 
                  type="text" 
                  size="small" 
                  icon={<EditOutlined />}
                  onClick={() => {
                    setBankCharInput(bankCharacter);
                    setEditingBankChar(true);
                  }}
                />
              )
            }
          >
            {editingBankChar ? (
              <div>
                <Input
                  value={bankCharInput}
                  onChange={e => setBankCharInput(e.target.value)}
                  placeholder="输入角色名"
                  style={{ marginBottom: 8 }}
                  maxLength={12}
                />
                <Space>
                  <Button 
                    type="primary" 
                    size="small" 
                    icon={<SaveOutlined />}
                    onClick={saveBankCharacter}
                  >
                    保存
                  </Button>
                  <Button 
                    size="small" 
                    onClick={() => setEditingBankChar(false)}
                  >
                    取消
                  </Button>
                </Space>
              </div>
            ) : bankCharacter ? (
              <div style={{ textAlign: 'center' }}>
                <img
                  src={`${API_BASE_URL}/api/getavatar?name=${encodeURIComponent(bankCharacter)}`}
                  alt={bankCharacter}
                  style={{ 
                    width: 96, 
                    height: 96, 
                    objectFit: 'contain',
                    marginBottom: 8,
                    background: 'var(--surface-subtle)',
                    borderRadius: 8
                  }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
                <div>
                  <Text strong style={{ fontSize: 16 }}>{bankCharacter}</Text>
                </div>
                {bankCharLoading ? (
                  <Text type="secondary">加载中...</Text>
                ) : bankCharInfo ? (
                  <div style={{ marginTop: 4 }}>
                    <Tag color="blue">{bankCharInfo.job}</Tag>
                    <Tag>Lv.{bankCharInfo.level}</Tag>
                  </div>
                ) : null}
                <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                  提取时请联系此角色
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
                <UserOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                <div>未设置银行角色</div>
                {isAdmin && (
                  <Button 
                    type="link" 
                    size="small"
                    onClick={() => setEditingBankChar(true)}
                  >
                    点击设置
                  </Button>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* 管理员标识 */}
      {isAdmin && (
        <Alert
          message={<><CrownOutlined /> 您是管理员，可以审核提取和转账申请、以及代理其他用户提取</>}
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 当前兑换汇率 */}
      <Card size="small" style={{ marginBottom: 16 }}
        title={<><SwapOutlined /> 当前兑换汇率</>}
        extra={isAdmin ? (
          <Button size="small" icon={<EditOutlined />} onClick={() => {
            rateForm.setFieldsValue({
              exchangeMesoToCoin: sysConfig.exchangeMesoToCoin,
              exchangeCoinToMeso: sysConfig.exchangeCoinToMeso,
            });
            setRateModalVisible(true);
          }}>设置汇率</Button>
        ) : null}
      >
        <Space size="large" wrap>
          <Statistic
            title="Meso → Coin（买入 1 Coin）"
            value={sysConfig.exchangeMesoToCoin || 0}
            suffix="Meso"
            valueStyle={{ fontSize: 18, color: '#faad14' }}
            formatter={v => Number(v).toLocaleString()}
          />
          <Statistic
            title="Coin → Meso（卖出 1 Coin）"
            value={sysConfig.exchangeCoinToMeso || 0}
            suffix="Meso"
            valueStyle={{ fontSize: 18, color: '#52c41a' }}
            formatter={v => Number(v).toLocaleString()}
          />
        </Space>
      </Card>

      <Tabs
        defaultActiveKey="my"
        size={isMobile ? 'small' : 'middle'}
        items={[
          {
            key: 'my',
            label: `我的记录 (${myRecords.length})`,
            children: renderRecords(myRecords, '暂无记录')
          },
          ...(isAdmin ? [{
            key: 'pending',
            label: <Badge count={pendingRecords.length} offset={[10, 0]}>待审核</Badge>,
            children: renderRecords(pendingRecords, '暂无待审核申请')
          }] : []),
          ...(isAdmin ? [{
            key: 'all',
            label: `全部记录 (${allRecords.length})`,
            children: renderRecords(allRecords, '暂无记录')
          }] : []),
        ]}
      />

      {/* 统一「新申请」弹窗（提取 / 转账 / 代理提取） */}
      <Modal
        title={<><ExportOutlined /> 新申请</>}
        open={applyModalVisible}
        onCancel={closeApplyModal}
        footer={null}
        destroyOnClose
        width={540}
      >
        <Segmented
          value={applyMode}
          onChange={(v) => { setApplyMode(v); applyForm.resetFields(); setApplyTarget(null); }}
          block
          options={[
            { label: '提取', value: 'withdraw', icon: <ExportOutlined /> },
            { label: '转账', value: 'transfer', icon: <UserOutlined /> },
            { label: '兑换', value: 'exchange', icon: <SwapOutlined /> },
            ...(isAdmin ? [{ label: '代理提取', value: 'proxy', icon: <CrownOutlined /> }] : []),
          ]}
          style={{ marginBottom: 16 }}
        />

        {applyMode === 'withdraw' && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message={<>我的余额 &nbsp; Meso: <Text strong>{userBalance.meso.toLocaleString()}</Text> &nbsp; Coin: <Text strong>{userBalance.coin.toLocaleString()}</Text> &nbsp;·&nbsp; Meso 提取收 2.5% 手续费，Coin 免手续费</>}
          />
        )}
        {applyMode === 'transfer' && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message={<>我的余额 &nbsp; Meso: <Text strong>{userBalance.meso.toLocaleString()}</Text> &nbsp; Coin: <Text strong>{userBalance.coin.toLocaleString()}</Text> &nbsp;·&nbsp; 转账无手续费，需管理员审核</>}
          />
        )}
        {applyMode === 'exchange' && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message={<>我的余额 &nbsp; Meso: <Text strong>{userBalance.meso.toLocaleString()}</Text> &nbsp; Coin: <Text strong>{userBalance.coin.toLocaleString()}</Text> &nbsp;·&nbsp; 兑换按整数 Coin，需管理员审核（汇率提交时锁定）</>}
          />
        )}
        {applyMode === 'proxy' && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            message="代理提取会直接从目标用户余额中扣除，无二次审核。请谨慎操作。"
          />
        )}

        <Form form={applyForm} layout="vertical" onFinish={handleApply}
          initialValues={{ exchangeDirection: 'meso_to_coin' }}>
          {/* 转账 / 代理提取：目标用户选择 */}
          {(applyMode === 'transfer' || applyMode === 'proxy') && (
            <Form.Item name="targetUser" label={applyMode === 'transfer' ? '收款用户' : '目标用户'}
              rules={[{ required: true, message: '请选择用户' }]}
              extra="支持输入账号名或角色 IGN 过滤">
              <Select
                placeholder="输入账号名或角色 IGN 搜索"
                showSearch
                onChange={(v) => setApplyTarget(allBalances.find(b => b.username === v) || null)}
                filterOption={(input, option) => (option?.searchKey || '').includes((input || '').toLowerCase())}
                options={allBalances
                  .filter(b => !b.isUnassigned && (applyMode !== 'transfer' || b.username !== currentUser?.username))
                  .map(b => {
                    const charNames = (b.characters || []).map(c => c.characterName).filter(Boolean);
                    const charLabel = charNames.length ? ` · IGN: ${charNames.join(', ')}` : '';
                    return {
                      value: b.username,
                      label: `${b.username}  (Meso: ${(b.mesoBalance || 0).toLocaleString()} / Coin: ${(b.coinBalance || 0).toLocaleString()})${charLabel}`,
                      searchKey: `${b.username} ${charNames.join(' ')}`.toLowerCase(),
                    };
                  })}
              />
            </Form.Item>
          )}
          {applyTarget && (applyMode === 'transfer' || applyMode === 'proxy') && (
            <Alert type="success" showIcon style={{ marginBottom: 12 }}
              message={<>{applyMode === 'transfer' ? '收款人' : '目标用户'} <Text strong>{applyTarget.username}</Text> 当前余额 &nbsp; Meso: <Text>{(applyTarget.mesoBalance || 0).toLocaleString()}</Text> &nbsp; Coin: <Text>{(applyTarget.coinBalance || 0).toLocaleString()}</Text></>}
            />
          )}

          {/* 兑换模式：方向 + 整数 Coin */}
          {applyMode === 'exchange' ? (
            <>
              <Form.Item name="exchangeDirection" label="兑换方向" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'meso_to_coin', label: `Meso → Coin（当前 1 Coin = ${(sysConfig.exchangeMesoToCoin || 0).toLocaleString()} Meso）` },
                  { value: 'coin_to_meso', label: `Coin → Meso（当前 1 Coin = ${(sysConfig.exchangeCoinToMeso || 0).toLocaleString()} Meso）` },
                ]} />
              </Form.Item>
              <Form.Item name="coinAmount" label="兑换 Coin 数量（整数）"
                rules={[{ required: true, message: '请输入 Coin 数量' }]}>
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  precision={0}
                  step={1}
                  inputMode="numeric"
                  placeholder="≥ 1 的整数"
                />
              </Form.Item>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={
                  exchangePreview.dir === 'meso_to_coin'
                    ? <>预计支付 <Text strong style={{ color: '#ff4d4f' }}>{exchangePreview.meso.toLocaleString()} Meso</Text>，获得 <Text strong style={{ color: '#faad14' }}>{exchangePreview.coin} Coin</Text></>
                    : <>预计支付 <Text strong style={{ color: '#ff4d4f' }}>{exchangePreview.coin} Coin</Text>，获得 <Text strong style={{ color: '#52c41a' }}>{exchangePreview.meso.toLocaleString()} Meso</Text></>
                }
              />
            </>
          ) : (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="mesoAmount"
                  label="Meso"
                  extra={applyMode === 'withdraw' && previewFee > 0 ? <Text type="secondary">手续费: {previewFee.toLocaleString()} (2.5%)</Text> : null}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={
                      applyMode === 'withdraw' ? userBalance.meso :
                      applyMode === 'transfer' ? userBalance.meso :
                      (applyTarget?.mesoBalance || undefined)
                    }
                    precision={0}
                    inputMode="numeric"
                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={v => v.replace(/,/g, '')}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="coinAmount" label="Coin">
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={
                      applyMode === 'withdraw' ? userBalance.coin :
                      applyMode === 'transfer' ? userBalance.coin :
                      (applyTarget?.coinBalance || undefined)
                    }
                    precision={0}
                    inputMode="numeric"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Form.Item name="note" label="备注"
            rules={applyMode === 'proxy' ? [{ required: true, message: '代理提取必须填写备注' }] : []}>
            <TextArea rows={2} placeholder={applyMode === 'proxy' ? '例如：管理员 代 XXX 手动取现' : '备注信息（可选）'} />
          </Form.Item>

          {applyMode === 'withdraw' && previewFee > 0 && (
            <Alert
              message={`实际到账: ${((mesoValue || 0) - previewFee).toLocaleString()} Meso`}
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
            />
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={closeApplyModal}>取消</Button>
              <Button type="primary" htmlType="submit" loading={loading} danger={applyMode === 'proxy'}>
                {applyMode === 'proxy' ? '提交代提取' : '提交申请'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 审核弹窗 */}
      <Modal
        title="审核提取申请"
        open={processModalVisible}
        onCancel={() => { setProcessModalVisible(false); setSelectedWithdrawal(null); }}
        footer={null}
        width={500}
      >
        {selectedWithdrawal && (
          <>
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}><Text type="secondary">申请人:</Text> <Text strong>{selectedWithdrawal.username}</Text></Col>
                <Col span={8}><Text type="secondary">Meso:</Text> <Text style={{ color: '#52c41a' }}>{selectedWithdrawal.mesoAmount?.toLocaleString() || 0}</Text></Col>
                <Col span={8}><Text type="secondary">Coin:</Text> <Text style={{ color: '#faad14' }}>{selectedWithdrawal.coinAmount || 0}</Text></Col>
              </Row>
              {selectedWithdrawal.mesoAmount > 0 && (
                <Row style={{ marginTop: 8 }}>
                  <Col span={12}><Text type="secondary">手续费:</Text> <Text type="danger">{selectedWithdrawal.mesoFee?.toLocaleString()} Meso</Text></Col>
                  <Col span={12}><Text type="secondary">实际到账:</Text> <Text type="success">{selectedWithdrawal.mesoActual?.toLocaleString()} Meso</Text></Col>
                </Row>
              )}
              {selectedWithdrawal.note && (
                <Row style={{ marginTop: 8 }}>
                  <Col><Text type="secondary">备注:</Text> {selectedWithdrawal.note}</Col>
                </Row>
              )}
            </Card>
            
            <Form form={processForm} layout="vertical" onFinish={handleProcess}>
              <Form.Item name="adminNote" label="管理员备注">
                <TextArea rows={2} placeholder="备注信息（可选）" />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => { setProcessModalVisible(false); setSelectedWithdrawal(null); }}>取消</Button>
                  <Button 
                    danger 
                    onClick={() => processForm.setFieldValue('action', 'reject')}
                    htmlType="submit"
                    loading={loading}
                  >
                    拒绝
                  </Button>
                  <Button 
                    type="primary"
                    onClick={() => processForm.setFieldValue('action', 'approve')}
                    htmlType="submit"
                    loading={loading}
                  >
                    批准
                  </Button>
                </Space>
              </Form.Item>
              <Form.Item name="action" hidden><Input /></Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* 管理员审核转账 Modal */}
      <Modal
        title={<><CrownOutlined /> 处理转账申请</>}
        open={transferProcessVisible}
        onCancel={() => { setTransferProcessVisible(false); setSelectedTransfer(null); }}
        footer={null}
        destroyOnClose
        width={520}
      >
        {selectedTransfer && (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={
                <Space direction="vertical" size={2}>
                  <span>转账 ID: <Text code>{selectedTransfer.id}</Text></span>
                  <span>从 <Text strong>{selectedTransfer.fromUser}</Text> → 到 <Text strong style={{ color: '#1890ff' }}>{selectedTransfer.toUser}</Text></span>
                  <span>金额：
                    {selectedTransfer.mesoAmount > 0 && <Text style={{ color: '#52c41a', marginRight: 8 }}>{selectedTransfer.mesoAmount.toLocaleString()} Meso</Text>}
                    {selectedTransfer.coinAmount > 0 && <Text style={{ color: '#faad14' }}>{selectedTransfer.coinAmount.toLocaleString()} Coin</Text>}
                  </span>
                  {selectedTransfer.note && <span>备注：{selectedTransfer.note}</span>}
                </Space>
              }
            />
            <Form form={transferProcessForm} layout="vertical" onFinish={handleProcessTransfer} initialValues={{ action: 'approve' }}>
              <Form.Item name="action" label="操作" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'approve', label: '批准（立即扣转到账户）' },
                  { value: 'reject', label: '拒绝' },
                ]} />
              </Form.Item>
              <Form.Item name="adminNote" label="管理员备注">
                <TextArea rows={2} placeholder="可选" />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => { setTransferProcessVisible(false); setSelectedTransfer(null); }}>取消</Button>
                  <Button type="primary" htmlType="submit" loading={loading}>提交</Button>
                </Space>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* 管理员审核兑换 Modal */}
      <Modal
        title={<><SwapOutlined /> 处理兑换申请</>}
        open={exchangeProcessVisible}
        onCancel={() => { setExchangeProcessVisible(false); setSelectedExchange(null); }}
        footer={null}
        destroyOnClose
        width={520}
      >
        {selectedExchange && (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={
                <Space direction="vertical" size={2}>
                  <span>兑换 ID: <Text code>{selectedExchange.id}</Text></span>
                  <span>用户：<Text strong>{selectedExchange.username}</Text></span>
                  <span>方向：<Text strong>{selectedExchange.direction === 'meso_to_coin' ? 'Meso → Coin' : 'Coin → Meso'}</Text></span>
                  <span>
                    {selectedExchange.direction === 'meso_to_coin'
                      ? <>支付 <Text style={{ color: '#ff4d4f' }}>{selectedExchange.mesoAmount.toLocaleString()} Meso</Text> → 获得 <Text style={{ color: '#faad14' }}>{selectedExchange.coinAmount} Coin</Text></>
                      : <>支付 <Text style={{ color: '#ff4d4f' }}>{selectedExchange.coinAmount} Coin</Text> → 获得 <Text style={{ color: '#52c41a' }}>{selectedExchange.mesoAmount.toLocaleString()} Meso</Text></>}
                  </span>
                  <span>锁定汇率：1 Coin = <Text strong>{(selectedExchange.rate || 0).toLocaleString()}</Text> Meso</span>
                  {selectedExchange.note && <span>备注：{selectedExchange.note}</span>}
                </Space>
              }
            />
            <Form form={exchangeProcessForm} layout="vertical" onFinish={handleProcessExchange} initialValues={{ action: 'approve' }}>
              <Form.Item name="action" label="操作" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'approve', label: '批准（按锁定汇率立即换币）' },
                  { value: 'reject', label: '拒绝' },
                ]} />
              </Form.Item>
              <Form.Item name="adminNote" label="管理员备注">
                <TextArea rows={2} placeholder="可选" />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => { setExchangeProcessVisible(false); setSelectedExchange(null); }}>取消</Button>
                  <Button type="primary" htmlType="submit" loading={loading}>提交</Button>
                </Space>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* 设置汇率 Modal（管理员） */}
      <Modal
        title={<><SwapOutlined /> 设置兑换汇率</>}
        open={rateModalVisible}
        onCancel={() => setRateModalVisible(false)}
        footer={null}
        destroyOnClose
        width={460}
      >
        <Alert type="info" showIcon style={{ marginBottom: 16 }}
          message="汇率修改后立即对新提交的兑换生效；已提交的待审兑换仍按其提交时锁定的汇率结算。" />
        <Form form={rateForm} layout="vertical" onFinish={handleSaveRates}>
          <Form.Item name="exchangeMesoToCoin" label="Meso → Coin：买 1 个 Coin 需要多少 Meso"
            rules={[{ required: true, message: '请输入汇率' }]}>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              precision={0}
              inputMode="numeric"
              formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={v => v.replace(/,/g, '')}
            />
          </Form.Item>
          <Form.Item name="exchangeCoinToMeso" label="Coin → Meso：卖 1 个 Coin 得到多少 Meso"
            rules={[{ required: true, message: '请输入汇率' }]}>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              precision={0}
              inputMode="numeric"
              formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={v => v.replace(/,/g, '')}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setRateModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={loading}>保存</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// Badge 组件需要额外引入
const Badge = ({ count, offset, children }) => (
  <span style={{ position: 'relative' }}>
    {children}
    {count > 0 && (
      <span style={{
        position: 'absolute',
        top: offset?.[1] || -8,
        right: offset?.[0] ? -offset[0] : -10,
        background: '#ff4d4f',
        color: 'white',
        borderRadius: 10,
        padding: '0 6px',
        fontSize: 12,
        minWidth: 18,
        height: 18,
        lineHeight: '18px',
        textAlign: 'center'
      }}>
        {count}
      </span>
    )}
  </span>
);

export default BankCounter;
